<script setup lang="ts">
import { Check, Loader2, Save, Store } from 'lucide-vue-next'
import type { DeliveryConnection, DeliveryStoreMapping } from '@/types/deliveryReviews'
import type { StoreMappingRowError } from '../deliveryReviewsApi'
import { apiErrorMessage, saveStoreMappings, storeMappingErrors } from '../deliveryReviewsApi'

const props = defineProps<{
  merchantId: number
  connection: DeliveryConnection
  merchantStores: { id: number; name: string }[]
}>()

const emit = defineEmits<{
  updated: [connection: DeliveryConnection]
}>()

/** reka-ui reserves the empty string, so "not linked" needs its own value. */
const NOT_LINKED = 'none'

const { t } = useI18n()

const selection = ref<Record<string, string>>({})
const isSaving = ref(false)
const isSaved = ref(false)
const rowErrors = ref<Record<string, StoreMappingRowError>>({})
/** Message shown above the save button when saving failed. */
const formError = ref<string | null>(null)

const accessibleStores = computed(() => props.connection.stores.filter((store) => !store.accessRevoked))

const savedValue = (storeId: number | null) => (storeId === null ? NOT_LINKED : String(storeId))

// A refresh of the stores ("Vérifier la connexion") keeps the choices not saved yet.
watch(
  () => props.connection.stores,
  (stores, previousStores) => {
    const previousSaved = new Map(previousStores?.map((store) => [store.externalStoreId, savedValue(store.storeId)]))
    selection.value = Object.fromEntries(
      stores.map((store) => {
        const current = selection.value[store.externalStoreId]
        const isUnsaved = current !== undefined && current !== previousSaved.get(store.externalStoreId)
        return [store.externalStoreId, isUnsaved ? current : savedValue(store.storeId)]
      })
    )
  },
  { immediate: true }
)

const hasChanges = computed(() =>
  accessibleStores.value.some((store) => selection.value[store.externalStoreId] !== savedValue(store.storeId))
)

/** Linked Pongo store missing from the merchant's list (deleted, or out of the user's scope): kept as an option so the value still shows. */
function isUnknownStore(storeId: number | null): storeId is number {
  return storeId !== null && !props.merchantStores.some((merchantStore) => merchantStore.id === storeId)
}

const triggerId = (externalStoreId: string) => `delivery-mapping-${props.connection.platform}-${externalStoreId}`

/** Name of the other platform store the Pongo store is already picked for, if any. */
function takenBy(externalStoreId: string, storeId: number): string | null {
  const other = accessibleStores.value.find(
    (store) => store.externalStoreId !== externalStoreId && selection.value[store.externalStoreId] === String(storeId)
  )
  return other?.name ?? null
}

function select(externalStoreId: string, value: unknown) {
  selection.value[externalStoreId] = String(value)
  isSaved.value = false
  formError.value = null
  delete rowErrors.value[externalStoreId]
}

async function save() {
  const mappings: DeliveryStoreMapping[] = accessibleStores.value.map((store) => {
    const value = selection.value[store.externalStoreId]
    return { external_store_id: store.externalStoreId, store_id: !value || value === NOT_LINKED ? null : Number(value) }
  })

  isSaving.value = true
  isSaved.value = false
  formError.value = null
  rowErrors.value = {}
  try {
    emit('updated', await saveStoreMappings(props.merchantId, props.connection.platform, mappings))
    isSaved.value = true
  } catch (error) {
    const rows = storeMappingErrors(error, mappings)
    if (rows) {
      rowErrors.value = rows
      formError.value = t('pages.deliveryReviews.mapping.invalid')
      const first = Object.keys(rows)[0]
      if (first) nextTick(() => document.getElementById(triggerId(first))?.focus())
    } else {
      formError.value = apiErrorMessage(error) ?? t('pages.deliveryReviews.mapping.error')
    }
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div>
      <p class="font-medium">{{ t('pages.deliveryReviews.mapping.title') }}</p>
      <p class="text-sm text-muted-foreground">{{ t('pages.deliveryReviews.mapping.description') }}</p>
    </div>

    <div
      v-if="connection.stores.length === 0"
      class="flex flex-col items-center gap-1 rounded-xl border border-dashed border-border p-6 text-center"
    >
      <Store class="size-5 text-muted-foreground" aria-hidden="true" />
      <p class="text-sm font-medium">{{ t('pages.deliveryReviews.mapping.empty.title') }}</p>
      <p class="text-sm text-muted-foreground">
        {{
          t('pages.deliveryReviews.mapping.empty.description', {
            action: t('pages.deliveryReviews.connection.check')
          })
        }}
      </p>
    </div>

    <template v-else>
      <BaseTip
        v-if="merchantStores.length === 0"
        severity="info"
        :text="t('pages.deliveryReviews.mapping.noPongoStore')"
      />

      <ul class="divide-y divide-border rounded-xl border border-border">
        <li
          v-for="store in connection.stores"
          :key="store.externalStoreId"
          class="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between"
        >
          <div class="min-w-0 sm:pt-2">
            <p :id="`${triggerId(store.externalStoreId)}-name`" class="truncate text-sm font-medium">
              {{ store.name }}
            </p>
            <p class="truncate text-xs text-muted-foreground">
              {{ [store.organizationName, store.address].filter(Boolean).join(' · ') }}
            </p>
          </div>
          <BaseChip
            v-if="store.accessRevoked"
            color="red"
            class="self-start sm:mt-2"
            :text="t('pages.deliveryReviews.mapping.revoked')"
          />
          <div v-else class="flex w-full flex-col gap-1 sm:w-64">
            <Select
              :model-value="selection[store.externalStoreId]"
              :disabled="isSaving"
              @update:model-value="select(store.externalStoreId, $event)"
            >
              <SelectTrigger
                :id="triggerId(store.externalStoreId)"
                class="w-full"
                :aria-label="`${t('pages.deliveryReviews.mapping.pongoStore')} · ${store.name}`"
                :aria-invalid="rowErrors[store.externalStoreId] ? true : undefined"
                :aria-describedby="
                  rowErrors[store.externalStoreId] ? `${triggerId(store.externalStoreId)}-error` : undefined
                "
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="NOT_LINKED">{{ t('pages.deliveryReviews.mapping.notLinked') }}</SelectItem>
                <SelectItem v-if="isUnknownStore(store.storeId)" :value="String(store.storeId)">
                  {{ t('pages.deliveryReviews.mapping.unknownStore') }}
                </SelectItem>
                <SelectItem
                  v-for="merchantStore in merchantStores"
                  :key="merchantStore.id"
                  :value="String(merchantStore.id)"
                  :disabled="takenBy(store.externalStoreId, merchantStore.id) !== null"
                >
                  {{ merchantStore.name }}
                  <span v-if="takenBy(store.externalStoreId, merchantStore.id)" class="text-xs text-muted-foreground">
                    ·
                    {{
                      t('pages.deliveryReviews.mapping.takenBy', {
                        store: takenBy(store.externalStoreId, merchantStore.id)
                      })
                    }}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <p
              v-if="rowErrors[store.externalStoreId]"
              :id="`${triggerId(store.externalStoreId)}-error`"
              class="text-xs text-destructive"
            >
              {{
                t(`pages.deliveryReviews.mapping.rowError.${rowErrors[store.externalStoreId]}`, {
                  action: t('pages.deliveryReviews.connection.check')
                })
              }}
            </p>
          </div>
        </li>
      </ul>
    </template>

    <div role="status" aria-live="polite">
      <BaseTip v-if="formError" severity="error" :text="formError" />
      <BaseTip v-else-if="isSaved" severity="success" :text="t('pages.deliveryReviews.mapping.saved')" />
    </div>

    <div v-if="accessibleStores.length > 0" class="flex items-center justify-end gap-3">
      <p v-if="hasChanges && !isSaving" class="text-xs text-muted-foreground">
        {{ t('pages.deliveryReviews.mapping.unsaved') }}
      </p>
      <Button variant="default-primary" :disabled="!hasChanges || isSaving" @click="save">
        <Loader2 v-if="isSaving" class="animate-spin" aria-hidden="true" />
        <Check v-else-if="isSaved && !hasChanges" aria-hidden="true" />
        <Save v-else aria-hidden="true" />
        {{ t('pages.deliveryReviews.mapping.save') }}
      </Button>
    </div>
  </div>
</template>
